import React from 'react';
import { Controller } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { MultiSelect } from '@/shared/components';
import { PlanVehicleRuleFourByFourField } from './PlanVehicleRuleFourByFourField';
import type { MultiSelectOption } from '@/shared/components';
import type { SelectFilterOption } from '@/features/vehiclesearch/types/manualsearch.types';
import type {
    PlanVehicleRuleBatchFormInput,
    PlanVehicleRuleBatchFormOutput,
} from '../schemas/planVehicleRuleBatch.schema';
import { PlanVehicleRuleDerivativeField, PlanVehicleRuleModelField } from './PlanVehicleRuleModelDerivativeFields';

interface Props {
    control: Control<PlanVehicleRuleBatchFormInput, unknown, PlanVehicleRuleBatchFormOutput>;
    makeOptions: MultiSelectOption[];
    transmissionOptions: MultiSelectOption[];
    fuelTypeOptions: MultiSelectOption[];
    modelOptions: SelectFilterOption[];
    derivativeOptions: SelectFilterOption[];
    modelValue: SelectFilterOption | null;
    derivativeValue: SelectFilterOption | null;
    onMakesChange: (ids: string[]) => void;
    onModelChange: (modelId: string) => void;
    hasSingleMake: boolean;
    hasMakeAndModel: boolean;
    derivativeSelected: boolean;
    isLoadingMakes: boolean;
    isLoadingModels: boolean;
    isLoadingTransmissions: boolean;
    isLoadingFuelTypes: boolean;
    busy: boolean;
}

export const PlanVehicleRuleBatchSelectionFields: React.FC<Props> = ({
    control,
    makeOptions,
    transmissionOptions,
    fuelTypeOptions,
    modelOptions,
    derivativeOptions,
    modelValue,
    derivativeValue,
    onMakesChange,
    onModelChange,
    hasSingleMake,
    hasMakeAndModel,
    derivativeSelected,
    isLoadingMakes,
    isLoadingModels,
    isLoadingTransmissions,
    isLoadingFuelTypes,
    busy,
}) => (
    <>
        <Controller
            name="make_ids"
            control={control}
            render={({ field, fieldState: { error: fieldError } }) => (
                <MultiSelect
                    label="Makes"
                    options={makeOptions}
                    value={field.value ?? []}
                    onChange={onMakesChange}
                    loading={isLoadingMakes}
                    disabled={busy}
                    error={fieldError?.message}
                />
            )}
        />

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Controller
                name="transmission_ids"
                control={control}
                render={({ field, fieldState: { error: fieldError } }) => (
                    <MultiSelect
                        label="Transmissions"
                        options={transmissionOptions}
                        value={field.value ?? []}
                        onChange={field.onChange}
                        loading={isLoadingTransmissions}
                        disabled={busy || derivativeSelected}
                        error={fieldError?.message}
                    />
                )}
            />

            <Controller
                name="fuel_type_ids"
                control={control}
                render={({ field, fieldState: { error: fieldError } }) => (
                    <MultiSelect
                        label="Fuel types"
                        options={fuelTypeOptions}
                        value={field.value ?? []}
                        onChange={field.onChange}
                        loading={isLoadingFuelTypes}
                        disabled={busy || derivativeSelected}
                        error={fieldError?.message}
                    />
                )}
            />
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <PlanVehicleRuleModelField
                options={modelOptions}
                modelValue={modelValue}
                onChange={onModelChange}
                derivativeSelected={derivativeSelected}
                disabled={busy || !hasSingleMake || derivativeSelected || isLoadingModels}
                placeholder={!hasSingleMake ? 'Select a single make first' : 'Any model'}
            />

            <PlanVehicleRuleDerivativeField
                control={control}
                options={derivativeOptions}
                derivativeValue={derivativeValue}
                disabled={busy || !hasMakeAndModel || derivativeOptions.length === 0}
                placeholder={
                    !hasSingleMake
                        ? 'Select a single make first'
                        : !hasMakeAndModel
                            ? 'Select a model first'
                            : 'Any derivative'
                }
            />
        </div>

        <Controller
            name="requires_4x4"
            control={control}
            render={({ field }) => (
                <PlanVehicleRuleFourByFourField
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    disabled={busy || derivativeSelected}
                />
            )}
        />
    </>
);
